import { useState, useEffect } from 'react';
import { X, Download, Printer, Loader2 } from 'lucide-react';

/**
 * Circuit Schedule PDF Preview Slide-over Component
 * Displays a PDF in a slide-over panel with download and print options
 */
const CircuitSchedulePdfPreview = ({ isOpen, onClose, pdfUrl, boardName }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && pdfUrl) {
      setLoading(true);
      setError(null);
    }
  }, [isOpen, pdfUrl]);

  const handleDownload = async () => {
    if (!pdfUrl) {
      console.error('No PDF URL available for download');
      setError('PDF URL not available. Please try generating again.');
      return;
    }
    
    try {
      console.log('Downloading PDF from URL:', pdfUrl);
      
      // Fetch PDF and trigger download
      const response = await fetch(pdfUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${boardName || 'Circuit-Schedule'}-Schedule.pdf`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up blob URL
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
      console.log('Download triggered successfully');
    } catch (err) {
      console.error('Download error:', err);
      setError(`Download failed: ${err.message}`);
    }
  };

  const handlePrint = () => {
    if (!pdfUrl) {
      setError('PDF URL not available. Please try generating again.');
      return;
    }
    
    try {
      // Open PDF in new window for printing
      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      } else {
        setError('Popup blocked. Please allow popups for this site.');
      }
    } catch (err) {
      console.error('Print error:', err);
      setError(`Print failed: ${err.message}`);
    }
  };

  const handleIframeLoad = () => {
    setTimeout(() => {
      setLoading(false);
    }, 500);
  };

  const handleIframeError = () => {
    setLoading(false);
    setError('Failed to load PDF preview. Please use download or print instead.');
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Slide-over Panel */}
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-4xl bg-white dark:bg-gray-800 shadow-2xl z-50 transform transition-transform duration-300 ease-out flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            {boardName ? `${boardName} - ` : ''}Circuit Schedule Preview
          </h2>
          <div className="flex items-center gap-3">
            {pdfUrl && (
              <>
                <button
                  onClick={handleDownload}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium transition-colors"
                >
                  <Download className="h-4 w-4" />
                  Download
                </button>
                <button
                  onClick={handlePrint}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 rounded-md text-sm font-medium transition-colors"
                >
                  <Printer className="h-4 w-4" />
                  Print
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* PDF Preview */}
        <div className="flex-1 relative bg-gray-100 dark:bg-gray-900 overflow-hidden">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <div className="text-center">
                <Loader2 className="h-8 w-8 text-blue-600 dark:text-blue-400 animate-spin mx-auto mb-2" />
                <p className="text-sm text-gray-600 dark:text-gray-400">Loading PDF...</p>
              </div>
            </div>
          )}
          
          {error && (
            <div className="absolute inset-0 flex items-center justify-center z-10 p-8">
              <div className="text-center max-w-md">
                <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                  <p className="text-sm text-red-600 dark:text-red-400 font-medium mb-1">Error:</p>
                  <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
                </div>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={handleDownload}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium"
                  >
                    Try Download
                  </button>
                  <button
                    onClick={onClose}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md text-sm font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {pdfUrl && !error && (
            <iframe
              src={pdfUrl}
              className="w-full h-full border-0"
              onLoad={handleIframeLoad}
              onError={handleIframeError}
              title="Circuit Schedule PDF Preview"
            />
          )}
        </div>
      </div>
    </>
  );
};

export default CircuitSchedulePdfPreview;

